<template>
  <b-container fluid
               class="px-lg-13 px-md-10 px-3 pt-6">
    <b-row>
      <b-col class="pb-1 pl-3 pl-md-0">
        <h4>Performance</h4>
      </b-col>
    </b-row>
    <b-row class="px-3 px-md-0">
      <b-col class="p-5 card">
        <div class="legend text-left mb-5 pl-7">
          <div v-for="(stock, index) in performanceGraphData.datasets"
               :key="stock.label"
               class="stock mr-3"
               @click="toggleGraph(index, stock)">
            <span :style="{ background: stock.backgroundColor}"
                  class="indicator mr-2">
            </span>
            <span class="stock-label"
                  :class="{ 'in-active': !stock.isActive }">{{stock.label}}
              <notification-icon :stock="stockData(stock.label)" />
            </span>
          </div>
          <div class="info-text">Displaying the last 6 months</div>
        </div>
        <charts :styles="chartStyle"
                :chart-data="performanceGraphData"
                ref="performanceChart"
                :options="chartOption"
                v-bind:class="{ blur:  performanceGraphData.datasets.length < 1  }"
                class="performanceChart"></charts>
        <div class="cardInfo"
             v-bind:class="{ show:  performanceGraphData.datasets.length < 1  }">
          <h4>Watch your stocks</h4>
          <p>
            Save stocks to your watchlist<br>
            so you can see historical trends
          </p>
        </div>
      </b-col>

    </b-row>
    <b-row class="mt-0 mt-lg-6 portfolio px-3 px-md-0">
      <b-col lg="12"
             xl="8"
             class="portfolio-breakdown order-xl-first order-lg-last px-md-3 mt-4 mt-lg-0">
        <alert-bar :showAlert="contactForm.messageSent" :message="contactForm.message" :variant="contactForm.messageVariant" />
        <b-row class="mb-1 mt-lg-6 mt-xl-0 mt-3">
          <b-col class="px-0 col-12">
            <contact-form inlineMessage="We're always looking to improve so we'd love to get your feedback."
                inlineLink="Provide Feedback"
                modalHeading="Send your Feedback"
                modalText="We're always looking to improve so we'd love to get your feedback"
                submitEvent="feedback-submit"
                cancelEvent="feedback-cancel"
                modalId="FeedbackContactForm"
                :showInCard="true"
                />
            <h4>Portfolio Breakdown</h4>
          </b-col>
          <form-select class="col-sm-6 publication-filter"
                       :label="filterOptions.publications.label"
                       :defaultFilterOption="filterOptions.publications.default"
                       :filterItems="publications"
                       @valuechange="selectPublication" />
          <form-select class="col-sm-6 recommendation-filter"
                       :label="filterOptions.recommendation.label"
                       :defaultFilterOption="filterOptions.recommendation.default"
                       :filterItems="filterOptions.recommendation.options"
                       @valuechange="selectRecommendation" />
        </b-row>
        <b-row>
          <b-col class="card p-0">
            <portfolio-tbl :table-data="stocks"
                           :fields="fields"
                           :select="selectStock"
                           v-if="isLoading || stocks.length > 0" />
            <b-col class="performance-empty-message align-self-center"
                   md="9"
                   lg="7"
                   xl="6"
                   v-if="stocks.length === 0
                   && !isLoading">
              <h4 class="px-2">No stock recommendations</h4>
              <p class="px-md-5 px-3">Unfortunately, there are no stock recommendations available at the moment. Please try again later.</p>
              <p class="px-5 py-2 link">
              <contact-form inlineLink="Having issues accessing your portfolio? Get in touch."
                modalHeading="Contact Us"
                modalText="Need to contact us? Use the form below to get in touch with us"
                submitEvent="contact-submit"
                cancelEvent="contact-cancel"
                modalId="NoStockContactForm"/>
              </p>
            </b-col>
          </b-col>
        </b-row>
      </b-col>
      <b-col lg="12"
             xl="4"
             sm="12"
             class="order-first pr-xl-0 pl-xl-6 px-lg-0">
        <b-row>
          <b-col lg="6"
                 xl="12"
                 class="watch-list-sec px-lg-3 px-0 mt-6 mt-lg-0 mt-xl-6">
            <h4>Watchlist</h4>
            <div class="card"
                 :style="[watchList.stocks.length < 4? { height: '25rem'} : {}]">
              <watch-list :list="watchList.stocks"
                          :click="selectStock"
                          v-if="watchList.stocks.length > 0" />
              <div class="empty-watch-list-msg my-18"
                   v-if="watchList.stocks.length === 0">
                <h4>Keep track of stocks</h4>
                <p class="px-13">You can now keep track of Guru recommended stocks by adding them to your watchlist.</p>
              </div>
            </div>
          </b-col>
          <!-- <b-col lg="6"
                 xl="12"
                 class="popular-stock mt-xl-4 mt-6 mt-lg-0 px-lg-3 px-0">
            <h4>Popular Stocks</h4>
            <div class="card">
              <div class="empty-popular-stock-msg my-18">
                <h4>Coming Soon</h4>
                <p class="px-13">
                  Unfortunately there are no popular stocks at the moment.
                </p>
              </div>
            </div>
          </b-col> -->
        </b-row>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapState } from 'vuex'
import $store from '@/store/index'
import Charts from '@/components/Charts'
import PortfolioTbl from '@/components/PortfolioTbl'
import FormSelect from '@/components/FormSelect'
import WatchList from '@/components/WatchList'
import ContactForm from '@/components/ContactForm'
import NotificationIcon from '@/components/NotificationIcon'
import AlertBar from '@/components/AlertBar'
import { CHART_OPTIONS, DEFAULT_CHART_DATA } from '@/utils/chartOptions'
import { STOCK_TABLE_HEADER, MAX_WATCH_LIST, RECOMMENDATION_OPTIONS } from './constants'

export default {
  components: {
    Charts,
    PortfolioTbl,
    FormSelect,
    WatchList,
    NotificationIcon,
    ContactForm,
    AlertBar
  },
  data () {
    return {
      chartOption: {
        ...CHART_OPTIONS
      },
      performanceGraphData: DEFAULT_CHART_DATA,
      chartStyle: {
        height: '480px',
        position: 'relative'
      },
      filterOptions: {
        publications: {
          label: 'Showing:',
          default: { id: -1, name: 'All publications' }
        },
        recommendation: {
          label: 'Filter by:',
          default: { id: -1, name: 'All recommendations' },
          options: RECOMMENDATION_OPTIONS
        }
      },
      fields: STOCK_TABLE_HEADER,
      publication: -1,
      recommendation: -1
    }
  },
  async mounted () {
    await $store.dispatch('publications/getPublicationsByAfId', {
      id: this.affiliateId,
      subs: this.subs
    })
    const subs = this.publications.map(publication => {
      return publication.id
    })

    await $store.dispatch('stocks/getStocksByAfId', { id: this.affiliateId, subs })
    this.renderPerformanceGraph()
  },
  computed: {
    ...mapState('customer', ['id', 'affiliateId', 'subs', 'contactForm']),
    ...mapState('publications', ['publications']),
    ...mapState('stocks', ['stocks', 'watchList', 'isLoading'])
  },
  methods: {
    async selectPublication ({ id }) {
      const subs = this.publications.map(publication => {
        return publication.id
      })
      this.publication = id
      id === -1
        ? await $store.dispatch('stocks/getStocksByAfId', { id: this.affiliateId, subs })
        : await $store.dispatch('stocks/getStocksByPublicationId', id)
      if (this.recommendation !== -1) $store.dispatch('stocks/getStocksByRecommendStatus', { id: this.recommendation, stocks: this.stocks })
      // tracking publiction filter event
      this.$ea.trackEvent({
        type: 'publication_filter',
        value: { pub_id: id.toString() }
      })
    },
    async selectRecommendation ({ id }) {
      const subs = this.publications.map(publication => {
        return publication.id
      })
      this.recommendation = id
      this.publication === -1
        ? await $store.dispatch('stocks/getStocksByAfId', { id: this.affiliateId, subs })
        : await $store.dispatch('stocks/getStocksByPublicationId', this.publication)
      if (id !== -1) $store.dispatch('stocks/getStocksByRecommendStatus', { id: id, stocks: this.stocks })
    },
    renderPerformanceGraph: async function () {
      let graphData = await $store.dispatch('stocks/getWatchList')
      if (graphData) this.performanceGraphData = graphData
    },
    selectStock: async function (stock) {
      let alertMessage = {
        'message': '',
        'messageSent': false,
        'messageVariant': ''
      }
      if (!stock.selected && this.watchList.stocks.length === MAX_WATCH_LIST) {
        alertMessage = {
          'message': 'You have reached the maximum of 10 stocks in your watchlist',
          'messageSent': true,
          'messageVariant': 'warning'
        }

        $store.dispatch('customer/showMessage', alertMessage)
        return
      }
      const stockPresent = (item) => item.symbol.includes(stock.symbol)
      if (!stock.selected && this.watchList.stocks.some(stockPresent)) {
        alertMessage = {
          'message': 'You already have that stock added to your watchlist',
          'messageSent': true,
          'messageVariant': 'warning'
        }
        $store.dispatch('customer/showMessage', alertMessage)
        return
      }
      $store.dispatch('customer/showMessage', alertMessage)

      const dataSets = this.performanceGraphData.datasets

      if (stock.selected) {
        const stockSelected = dataSets.find(data => (data.label === stock.alias))
        if (typeof stockSelected !== 'undefined') {
          stock.color = stockSelected.backgroundColor[0]
        }
        const stockIndex = dataSets.indexOf(stockSelected)
        if (~stockIndex) {
          dataSets.splice(stockIndex, 1)
        }
      }
      const graphData = await $store.dispatch('stocks/updateWatchList', stock)

      if (graphData) {
        if (dataSets.length === 0) {
          this.performanceGraphData = graphData
        } else {
          dataSets.unshift(graphData.datasets[0])
        }
      }

      this.$refs.performanceChart.$data._chart.update()
    },
    stockData: function (label) {
      let stock = this.watchList.stocks.filter(stock => stock.symbol === label)
      return stock[0]
    },
    toggleGraph: function (index, stock) {
      const chart = this.$refs.performanceChart.$data._chart
      const meta = chart.getDatasetMeta(index)
      meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null
      stock.isActive = !meta.hidden
      chart.update()

      // tracking stock legend click event
      this.$ea.trackEvent({
        type: 'stock_legend',
        value: { stock: stock.label, isActive: stock.isActive }
      })
    },
    scrollToPortfolio: function () {
      document.getElementsByClassName('portfolio')[0].scrollIntoView()
    }
  }
}
</script>

<style lang='scss' scoped>
.legend {
  color: $color-text-black;
  letter-spacing: 0.2px;
  line-height: 22px;
  .stock {
    display: inline-block;
    cursor: pointer;
    .indicator {
      padding: 0 0.4rem 0 1rem;
      border-radius: 7.5px;
    }
    .stock-label {
      font-size: 0.9rem;
    }
    .in-active {
      text-decoration: line-through;
    }
  }
  .info-text {
    float: right;
    font-size: 0.9rem;
    color: $color-text-grey;
  }
}
h4 {
  font-size: 1.5em;
  color: $color-text-blue;
  font-weight: bold;
  letter-spacing: 0.17px;
  line-height: 32px;
  text-align: left;
}
.performanceChart {
  transition: all 1100ms;
}
.blur {
  filter: blur(2px);
}
.card {
  background-color: $color-cord-bg;
  box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  border: none;
}
.cardInfo {
  position: absolute;
  background: #272727;
  color: white;
  padding: 20px;
  border-radius: 5px;
  -webkit-transition: all 500ms;
  transition: all 1100ms;
  display: none;
  width: 35%;
  margin: 0;
  transform: translate(-50%, -50%);
  left: 50%;
  top: 50%;
  &.show {
    display: block;
  }
  h4 {
    color: white;
    text-align: center;
  }
  @media (max-width: 925px) {
    width: 45%;
  }
  @media (max-width: 690px) {
    width: 55%;
  }
}

.performance-empty-message {
  padding: 295px 0 323px 0;
  h4 {
    font-size: 1.5rem;
    text-align: center;
    color: $color-text-grey;
  }
  .link {
    cursor: pointer;
    color: #4b83d6;
    text-decoration: underline;
  }
}

.portfolio-breakdown {
  @include media-breakpoint-down(xs) {
    .recommendation-filter {
      text-align: left !important;
      padding-left: 0;
    }
  }
  .publication-filter {
    text-align: left;
    padding-left: 0;
  }
  .recommendation-filter {
    text-align: right;
    padding-right: 0;
  }
}

.popular-stock,
.watch-list-sec {
  .empty-watch-list-msg,
  .empty-popular-stock-msg {
    h4 {
      text-align: center;
      color: $color-text-grey;
    }
  }
}
.portfolio-import-coming-soon-msg {
  h4 {
    text-align: center;
  }
  p {
    letter-spacing: 0.3px;
    line-height: 24px;
  }
  .notofy-selection {
    color: #50709d;
  }
}

.comparison {
  p {
    text-align: left;
  }
  .portfolio-segment {
    text-align: left;
    h5 {
      font-weight: 600;
      letter-spacing: 0.17px;
      line-height: 26px;
      font-size: 1.3rem;
    }
    .indicator {
      display: inline-block;
      span {
        &:nth-child(1) {
          color: #31568c;
          font-weight: 600;
          font-size: 1.3rem;
        }
        &:nth-child(2) {
          font-size: 0.9rem;
          letter-spacing: 0.2px;
          line-height: 22px;
        }
        &:nth-child(3) {
          font-size: 0.95rem;
          letter-spacing: 0.1px;
          line-height: 22px;
          float: right;
          a {
            color: #31568c;
            font-weight: 600;
            text-decoration: underline;
          }
        }
      }
    }
    p {
      color: #8d8e8f;
      font-size: 0.95rem;
      letter-spacing: 0.2px;
      line-height: 22px;
    }
  }
}
</style>
